/**
 * The airframe's own constants — how a `.DAT` weight/drag rating becomes a mass
 * and a wing, what the lift curve and the drag polar look like, and what
 * sideslip does. Split out of `tunables.ts` on the established sibling-register
 * pattern (`stall-tunables.ts`, `gear-tunables.ts`, `gunnery-tunables.ts`),
 * which is what keeps "how an aeroplane flies" one file's worth of reading;
 * they merge straight back into `DEFAULT_TUNABLES`, so nothing downstream sees
 * more than one table. No value moved in the split.
 *
 * The provenance markers are `tunables.ts`'s — VERIFIED / REPORTED,
 * CALIBRATION-PENDING, MEASURED-DOSBOX — and that module's header explains
 * them.
 */

export const AERO_TUNABLES = {
  /**
   * Mass per unit of the `.DAT` weight/drag rating (offset 36). CALIBRATION-PENDING,
   * but well anchored: ×16.5 reproduces real loaded weights closely across the
   * scouts (Pup 35→578 kg vs 556 real, Camel 40→660 vs 659, D.VII 56→924 vs 906).
   * The AI-only heavies share copy-pasted drag ratings (flight-tunables.md §1.1)
   * so their masses are class approximations, not aircraft figures.
   */
  massPerDragUnitKg: 16.5,
  /** Wing loading used to back out a reference area from mass. CALIBRATION-PENDING; 33 kg/m² reproduces WWI scout wing areas to a few percent. */
  wingLoadingKgPerM2: 33,
  /** Max lift coefficient per unit of the stall/lift field (offset 24). MEASURED-DOSBOX: the original's Camel sustains 1-g flight at 42.5 mph at 8,060 ft, so the 700 class carries CL ≥ 1.86 — higher than any real cellule (flight-tunables.md §8.1). */
  clMaxPerStallUnit: 0.00268,
  /** Lift-curve slope before the break, per radian. CALIBRATION-PENDING; 5.0 is typical for a low-aspect-ratio biplane cellule. */
  liftCurveSlopePerRad: 5.0,
  /**
   * Wing incidence — the angle between the wing's own zero-lift line and the
   * fuselage reference the game stores as pitch attitude, in radians. The
   * aerodynamics see `α_body + wingIncidenceRad`, so the wing is already
   * carrying about 0.48 of `C_L` with the fuselage dead level.
   *
   * **MEASURED-DOSBOX**, and it is the open register's item 17 finally closed.
   * `aero.ts`'s lift curve used to run through the origin — `C_L = a·α` with no
   * camber and no incidence — which is not what any WWI aerofoil does and, more
   * to the point, not what `RB.EXE` does. The calibration session's Camel polar
   * (re-sessions/calib/NOTES.md §4.1) is flown at full throttle at 8,060 ft and
   * indexed by pitch *attitude*, and the original flies **level at −1.2°** and
   * **climbs 230 ft/min at 0.0°**. A lift curve through the origin cannot do
   * that: at zero body α it makes zero lift, so a level fuselage descends.
   *
   * Four anchors fix the value, and they agree far better than the fit needed
   * them to. Matching the port's *airspeed* to the original's at each point and
   * reading off the attitude difference gives:
   *
   * | anchor | original | port, pre-fix | offset |
   * |---|---:|---:|---:|
   * | Camel, hands off | 0.0° @ 95.8 mph | +5.90° | 5.90° |
   * | Camel, max level speed | −1.2° @ 99.7 mph | +4.75° | 5.95° |
   * | SPAD XIII, hands off | 0.0° @ 111.8 mph | +4.82° | 4.82° |
   * | SPAD XIII, max level speed | −1.5° @ 115.3 mph | +3.95° | 5.45° |
   *
   * The Camel's three cruise points (95.8, 99.7 and 104.4 mph) land on 5.90,
   * 5.95 and 6.00° — a constant to 0.1° across a 9 mph span, which is the
   * signature of an *attitude* offset rather than of a drag or power error,
   * since a power error would grow with speed and a drag error would shrink.
   * 5.5° is the mean of the four and splits the two airframes' fits.
   *
   * **Global rather than per-aircraft, deliberately.** Real incidence is an
   * airframe design number, but no `<PLANE>.DAT` field carries one (the field
   * map in docs/formats/missions-campaign.md is complete over the 176-byte
   * record) and only two aircraft have ever been flown in the original, so a
   * per-type table would be 31 invented numbers wearing two measured ones. It
   * follows the shape of the original's other global-with-exceptions constants,
   * e.g. the offset 26–27 rudder-authority coefficient.
   *
   * **It ships at 0, which is the pre-AD-007o behaviour, and that is a staging
   * decision rather than a doubt about the number.** `MEASURED_WING_INCIDENCE_RAD`
   * below carries the measured 5.5°, `flight-incidence.test.ts` flies every
   * anchor in the table at that value, and flipping this default to it is a
   * one-line change. What is not one line is the re-calibration it pulls in:
   * turning it on moves the *attitude* every other MEASURED-DOSBOX rail was fitted
   * against, and four of them were absorbing part of the missing incidence.
   * Measured on this branch, with everything else held:
   *
   * | rail | band | at 5.5° incidence |
   * |---|---|---|
   * | AD-007h, the 5.3 s banked pull's 173° swing | ±5 % | 10.6 % |
   * | AD-007j, slip's 8.22 W/kg energy bleed | ±5 % | 17.3 % |
   * | AD-007m, the gyroscopic pitch↔yaw ratio | < 0.30 | 0.356 |
   * | AD-007n R6, the pull's 49.6° attitude | > 40° | 39.4° |
   * | §4.5's zoom, which the original does not spin | no spin | spins |
   *
   * Every *angle-of-attack* rail (R2, R4, R5, R7) still passes, which is the
   * evidence that the aerodynamics did not move and only the attitude mapping
   * did — exactly what this change is supposed to do. The obvious shortcut is
   * wrong and was tried: raising `alphaFollowStallFraction` to put the attitude
   * rails back puts the wing genuinely deeper into its curve and breaks R2, R7
   * and the departure census instead (measured at 0.45). So the four rails want
   * re-deriving against their own traces with the incidence in place, plus a
   * re-cut replay tape, a `SNAPSHOT_VERSION` bump and ~20 seed-pinned combat and
   * mission fixtures re-pinned. That is a lane, and it is named in the roadmap.
   */
  wingIncidenceRad: 0.096,
  /** Fraction of CLmax still available deep in the stall. CALIBRATION-PENDING. */
  postStallClFactor: 0.55,
  /** Angle over which lift decays past the break. CALIBRATION-PENDING. */
  postStallSpreadRad: 0.21,
  /** Extra profile drag per radian of post-break angle of attack. CALIBRATION-PENDING. */
  stallDragPerRad: 1.6,
  /** Induced-drag factor k in `CD = CD0 + k·CL²`. CALIBRATION-PENDING; 0.10 ≈ AR 4, e 0.8. */
  inducedDragFactor: 0.1,
  /** Floor for the calibrated parasite drag, in case a spec's power/drag pair would imply a non-physical CD0. */
  minParasiteDrag: 0.02,
  /**
   * Extra drag coefficient per radian *squared* of sideslip (AD-007i) — the
   * energy half of what slip does, and the only thing slip contributes to the
   * force sum. β² because the penalty is symmetric in slip direction and has to
   * vanish smoothly at zero, which is also what keeps trimmed flight untouched.
   * It replaced the deleted `sideforcePerRad`, a *translational* span-axis
   * force: perpendicular to the flight path, so it bled no speed and curved the
   * path instead.
   *
   * MEASURED-DOSBOX as of AD-007j, where it stops being AD-007i's "one
   * genuinely unmeasured number". Experiment 1 held full right rudder on the
   * Camel for 18 s at full throttle, 8,100 ft: **97.8 → 90.6 mph with the climb
   * falling 237 → 209 ft/min**.
   *
   * The fit is on *energy*, not on the 90.6 mph endpoint, because the port and
   * the original are not in the same trim at the start (the original was
   * settled at 97.8 mph and +237 ft/min; the port's calibration start is not,
   * and matching the endpoint would be fitting slip drag to a trim difference).
   * Read as a rate against a baseline holding both, the trace's skid costs
   * `½Δ(v²)/Δt + g·Δclimb` ≈ **8.22 W/kg**. What ships is the value that makes
   * the port's own 18 s hold cost that, measured the same way against its own
   * rudder-free run — the behaviour, which is how every rail in
   * `flight-calibration.test.ts` is written.
   *
   * **Why AD-007l moved it 4.15 → 8.75, and why that is a result rather than a
   * side effect.** This coefficient multiplies β², so it is only meaningful
   * against a stated slip angle, and AD-007l changed the slip angle the port
   * settles at in exactly this manoeuvre: refitting `pathAlignRatePerRad`
   * against §8.5.3's three bias-corrected traces takes the equilibrium skid from
   * 2.8° to the measured **2.0°**, and holding the same measured 8.22 W/kg
   * across a slip 0.71× as large needs a coefficient 1/0.71² ≈ 2× as big. The
   * interesting part is where the two independent fits now sit. Fitting the
   * *drag law* instead — the trace's own ΔCD ≈ 0.0097, read at 2.0° rather than
   * at AD-007j's 3.2° — gives **8.0**, against 3.1 at the old slip angle. Under
   * AD-007j the energy fit and the drag-law fit disagreed by 34 % and the note
   * had to say so; at the measured slip angle they agree within 9 %, which is
   * the first real evidence the β² *shape* is right rather than merely assumed.
   * It is still one slip angle: an extrapolation far from ~2° is still an
   * extrapolation, and this coefficient is large enough there to matter.
   *
   * **AD-007o moves it 8.75 → 10.25, and the reason is the start state rather
   * than the slip.** The paragraph above notes that "the port's calibration
   * start is not in the original's trim" and fits on energy to work around it.
   * That workaround was load-bearing in a way nobody had measured: the
   * calibration harness spawned *untrimmed*, and an untrimmed spawn is a
   * different aeroplane at every wing incidence — at 0° its wing makes no lift
   * at all. Flown from the trimmed start every one of these traces actually
   * begins from, the bleed comes out **incidence-independent to 0.3 %** (7.13
   * W/kg at 0°, 7.13 at 5.5°) where it had swung 17.3 % across that sweep. The
   * 17.3 % was the harness, not the slip.
   *
   * What that leaves is a real 13 % shortfall against the measured 8.22 that the
   * old start state had been masking, and 10.25 is where the port's own 18 s
   * hold costs **8.23 W/kg** measured the same way. Neither of the two rails
   * this shares the manoeuvre with moves: the equilibrium skid stays at exactly
   * **2.00°** and the steady yaw rate at **4.39 °/s** at every value tried from
   * 8.75 to 12, because both are set by `pathAlignRatePerRad` and the rudder
   * command rather than by the drag. The drag-law cross-check at 2.0° now reads
   * ΔCD = 0.0125 against the trace's 0.0097 — a 29 % spread where AD-007l
   * reported 9 %, which is the honest cost of this refit and is recorded rather
   * than smoothed.
   */
  slipDragPerRad2: 10.25,
  /**
   * Kinematic path-alignment rate per radian of sideslip, 1/s (AD-007j, refit
   * by AD-007l) — the flight path swinging onto the nose, applied to the
   * *velocity* only, and **the whole gain**: there is no speed scaling any more.
   *
   * MEASURED-DOSBOX. flight-tunables.md §8.5.3 held full rudder at three trims
   * on the Camel and read the steady state at each: 4.49 °/s of attitude yaw at
   * 88.2, 71.1 and 61.1 mph — identical to three significant figures — against
   * a steady β of 1.95°, 1.84° and 1.77°. In equilibrium the path turns at
   * exactly the nose's rate, so `k = ω/β` reads 2.30, 2.44 and 2.54 /s across
   * the three, and 4.49 ÷ 2.0 ≈ **2.2 /s** at the session's own summary β.
   * The value shipped is fitted so that the port's *flown* equilibrium slip
   * lands on the measured ≈2.0°, which is the behaviour the trace pins; the
   * three individual reads bracket it either side.
   *
   * This is a refit against session 3's traces, not a carry-forward. AD-007j's
   * 1.4 came from reading β ≈ 3.2° off a single earlier trace whose raw heading
   * lag was 8.5° before the mid-interval bias correction; §8.5.3 applies that
   * correction properly to three fresh traces and gets ≈2.0°, and the gain is
   * the ratio, so it moves with it.
   *
   * **The speed scaling is deleted, not zeroed.** AD-007j shipped
   * `k·β·(v/43.7)` on a physical argument (side force ∝ q·β, path rate ∝ v·β)
   * with one speed behind it. §8.5.3 is the second and third speed and it
   * refutes the shape outright: linear-in-v predicts β at 61 mph to be 1.45× its
   * 88 mph value and it measures 0.91×, excluded by a factor of 1.6, with a
   * best-fit exponent of +0.27 — zero inside a ±0.15° β measurement.
   * `pathAlignReferenceSpeedMps` left the register with the shape, on the rule
   * that took out `sideforcePerRad` and `weathercockYawRatePerRad`: a parameter
   * of a refuted shape is an invitation to the double-pathway bug.
   *
   * There is deliberately no weathercock counterpart either — same rule, same
   * reason.
   *
   * The *axis* the rate turns the velocity about is the aircraft's own yaw
   * axis, not the world vertical — the two are the same thing at the zero bank
   * every one of these traces was flown at, and `yawAboutBodyUp`'s note says why
   * only one of them closes β once the wings are not level.
   */
  pathAlignRatePerRad: 2.2,
  /**
   * α-relaxation rate, 1/s — how fast the attitude closes onto the angle of
   * attack the pilot's load-factor command implies (AD-007i, **ungated and
   * re-referenced by AD-007n change 2**). The pitch half of what the relative
   * wind does to the attitude. It outlived AD-007i's yaw half, which AD-007j
   * deleted: nothing in either DOSBox experiment bears on pitch, and
   * experiment 1 re-confirmed rudder→pitch coupling is exactly zero.
   *
   * **AD-007i's `1 − airspeedAuthority` gate is gone.** It made the term
   * identically zero above 42.5 mph, i.e. everywhere the game is played, and
   * `docs/engineering/flight-feel-findings.md` §2 measures the original's α
   * inside −1.4…+5° through three manoeuvres from 50 to 101 mph. What acts in
   * its place is `alphaRelaxLowSpeedGain` (the same shape, now multiplied *on
   * top* rather than switching the term off) and `alphaReferenceRad` (what the
   * relaxation aims at, which is the current α with the stick centred and so
   * leaves trim bit-constant).
   *
   * **Refit 0.9 → 4.4 by AD-007n**, because the quantity it sets changed
   * meaning. It is now the gain of the loop that bounds α under back pressure:
   * the elevator still commands `q` = 22.1 °/s of body pitch rate on the Camel,
   * and in equilibrium the angle of attack sits `q/k` above the reference, so
   * `k` *is* the steady α excess a full-back pull is worth. 4.4 /s puts that
   * excess at 5.0° — the width of the original's own measured α band — where
   * AD-007i's 0.9 would put it at 24.6°, which is the departure this AD exists
   * to remove. CALIBRATION-PENDING: the original exposes no α, so the fit is on
   * the *behaviour* (findings §2's traces) rather than on a read number.
   *
   * The §4.5 anchors it was originally fitted to still hold — see
   * `alphaRelaxLowSpeedFloor` for the pair.
   */
  alphaRelaxRatePerRad: 8,
  /**
   * How much of the α-relaxation survives when the airflow has gone — the term
   * is scaled by `floor + (1 − floor)·airspeedAuthority` (AD-007n change 2).
   *
   * This is what is left of AD-007i's gate after AD-007n ungated it, with its
   * sign the other way up. AD-007i faded the relaxation *in* as the controls
   * died; it fades **out** instead, on the same argument that fades the pitch
   * and roll commands — a wing in slow air does not weathercock the nose onto
   * the flight path any more than an elevator in slow air deflects it.
   *
   * The floor is not cosmetic, and it is bounded from both sides by measured
   * behaviour. At **1** (no fade) α is pinned near its reference at every speed
   * and the wing can never break, which would take §4.5's own "asymmetric entry,
   * rudder held at the stall" out of the game along with the departures this AD
   * removes — the spin rails go from ten types to none. At **0** the attitude
   * freezes at the top of a zoom and the Camel tail-slides to α = −137°, which
   * AD-007i measured and which the original does not do. CALIBRATION-PENDING,
   * fitted between the two against §4.5's sag anchors and the spin-entry census.
   */
  alphaRelaxLowSpeedFloor: 0.02,
  /**
   * AD-007n change 2: the ceiling on `alphaReferenceRad`, as a fraction of the
   * stall break. The elevator can ask the wing for an angle of attack up to this
   * much of the break and no further; past it the pull goes on being flown by
   * the produced path curvature with the nose still near the path, which is what
   * the original does at speeds where its own lift curve has run out (1.66 g at
   * 50 mph against a 1.38 g aerodynamic maximum).
   *
   * This is the single number that takes the every-airframe-stalls-inside-four-
   * seconds census to zero, and it is deliberately a *fraction of the break*
   * rather than an absolute angle so it scales with the per-aircraft lift class
   * offset 24 sets.
   *
   * CALIBRATION-PENDING, and **the measurement that would settle it now exists
   * and is not taken — deliberately, and this note is the record of why.**
   *
   * The value this number *should* have is measurable, which the old note here
   * denied ("the original exposes no α"). The original exposes an attitude and a
   * position, and α is the angle between the attitude and the flight path those
   * two give. Read that way, its `PITCH1` trace — wings-level full back stick
   * from cruise — has the Camel's **body** α climb −1.29° → **+4.89°** over
   * three seconds and level off, and `PULL1`'s peaks at +4.40°. The wing sits
   * `wingIncidenceRad` above the fuselage, so what the original's elevator asks
   * the *wing* for is 9.9–10.4°, against the Camel's 21.50° break:
   *
   * | anchor | body α | wing α | ÷ break |
   * |---|---:|---:|---:|
   * | `PITCH1`, the wings-level pull | +4.89° | 10.39° | **0.483** |
   * | `PITCH1`, AD-007n's quoted band | +4.7° | 10.20° | **0.474** |
   * | `PULL1`, the 27°-banked pull | +4.40° | 9.90° | **0.461** |
   *
   * So the measured value is **0.48**, and at 0.48 the port's own α trace lies
   * on the original's instead of 3° under it, and R6's attitude at 3.0 s goes
   * 39.4° → 46.4° against a measured 49.6°. AD-007o's note that raising this
   * "breaks R2, R7 and the departure census" turns out to be an artefact of the
   * comparison rather than of the value: those rails read `readout.alphaRad`,
   * which is the *wing's* angle, against bands taken from the original's *body*
   * α, and that is only the same number while the incidence is zero. Corrected
   * to body-against-body, R2 reads 3.9° against its ≤5° band and R7 shows no
   * departures at any fraction from 0.30 to 0.52.
   *
   * **0.48 does not survive being flown, and 0.33 is what ships (AD-007p,
   * 2026-08-27).** The derivation above is a ratio of *body* α to the break, and
   * it assumes the port's α responds to this number the way the original's does.
   * It does not, and the original's own two traces disagree about it once the
   * port is asked to reproduce both: `PULL1`'s peak body α reaches the measured
   * +4.40° at a fraction of ≈0.39, while `PITCH1`'s attitude swing is still
   * short of its 49.5° at 0.52. One global constant cannot land both.
   *
   * So the value is chosen against **every** measured band this number touches
   * rather than against one, swept through the real integrator:
   *
   * | fraction | R6 swing (49.5) | `PULL1` α_body (4.40) | turn rate (32.6 ±5 %) | R4 peak (< 40) | zoom worst α (< 130) |
   * |---:|---:|---:|---:|---:|---:|
   * | **0.30**, was | 41.2 (−16.7 %) | 2.33 | −4.6 % | 38.4 | 99 |
   * | **0.33**, ships | **42.3 (−14.4 %)** | 2.94 | **−2.6 %** | 39.0 | 114 |
   * | 0.35 | 43.1 (−12.9 %) | 3.35 | −1.3 % | 39.4 | 124 |
   * | 0.40 | 45.0 (−9.1 %) | 4.37 | +1.9 % | 40.4 | **155** |
   * | 0.48 | 48.0 (−3.1 %) | **6.04** | **+6.8 %** | **41.8** | **185** |
   *
   * What binds is the last column, and it is not a taste: past ≈0.34 the port
   * goes over the top of §4.5's zoom steeply enough to **slide backwards**, α
   * past 130° and on to 185°, which the original never does at any point of the
   * 18.5 s trace. Every extra degree of commanded α buys R6 and costs the top of
   * the zoom. 0.33 is the most that is free, and it improves the freshly
   * re-derived AD-007h turn rate from −4.6 % to −2.6 % on the way.
   *
   * **AD-007o's stated blocker was not the one.** It priced the zoom as an open
   * *energy* budget — "the port mushes over the top at 34 mph where the original
   * does 103" — and flown against the archived trace both aeroplanes hold
   * specific energy to within 1 %, with the port holding **more** at every phase.
   * The 69 mph is a distribution difference: the port is 320 ft higher at the
   * same point because it does not come down. What it is really short of is the
   * **roll-off** — the original's Camel rolls to 88° of bank at the top with no
   * aileron, `cos φ` kills the pull, and its nose stops at 70° where the port's
   * goes to 88°. That is measured, it is a lane of its own, and it is what
   * unblocks the rest of this number. AD-007p §5.
   */
  alphaFollowStallFraction: 0.33,

} satisfies Record<string, number>;

/**
 * The wing incidence the DOSBox-X calibration session's own Camel and SPAD XIII
 * polars imply — 5.5°, fitted to four anchors that agree to 0.6° across two
 * airframes. See `wingIncidenceRad` above for the table, for why the shipped
 * default is 0 instead, and for what turning it on costs.
 *
 * Exported so `flight-incidence.test.ts` can fly the anchors at the measured
 * value without hard-coding it, on the same pattern as
 * `WIND_MANUAL_REFERENCE_MPS`: a measured reference the tests are written
 * against is a named constant, never a literal repeated at each use.
 */
export const MEASURED_WING_INCIDENCE_RAD = 0.096;

/**
 * Deliberately no `AERO_TUNABLE_KEYS` alongside the other sibling registers:
 * this block straddles the provenance line — `clMaxPerStallUnit`,
 * `pathAlignRatePerRad` and `slipDragPerRad2` are MEASURED-DOSBOX while the
 * rest of the polar is still CALIBRATION-PENDING — so its keys are listed
 * individually in `tunables.ts`'s two registers rather than swept into one.
 */
